import React from 'react';
import PropTypes from 'prop-types';
import { Button, Tooltip, Popover, Divider } from 'antd';

import {
  CircleTickIcon,
  FailedIcon,
  TrashIcon,
  CloseIcon,
  RestoreIcon,
  DeleteIcon,
} from 'components/Icons';
import BulkTagsPopover from './components/BulkTagsPopover';

import './BulkActionsBar.scss';

/**
 * Bulk actions bar
 * @param {*} param0
 * @param {number} selectedCount - Selected count
 * @param {boolean} trash - Trash
 * @param {function} onApprove - On approve
 * @param {function} onDecline - On decline
 * @param {function} onRestore - On restore
 * @param {function} onAddOrRemoveTags - On add or remove tags
 * @param {function} onDelete - On delete
 * @param {function} onClearSelection - On clear selection
 * @param {boolean} isBulkActionLoading - Is bulk action loading
 * @param {object} bulkTagsPopover - Bulk tags popover
 * @param {function} setBulkTagsPopover - Set bulk tags popover
 * @param {array} tagsFilter - Tags filter
 * @param {function} onBulkTagsConfirm - On bulk tags confirm
 * @param {function} fetchTags - Fetch tags
 */
const BulkActionsBar = ({
  selectedCount,
  trash,
  onApprove,
  onDecline,
  onRestore,
  onAddOrRemoveTags,
  onDelete,
  onClearSelection,
  isBulkActionLoading,
  bulkTagsPopover,
  setBulkTagsPopover,
  tagsFilter,
  onBulkTagsConfirm,
  fetchTags,
}) => {
  const hasSelection = selectedCount > 0;
  const buttonsDisabled = !hasSelection || isBulkActionLoading;
  const tags = (tagsFilter || []).filter((t) => t._id !== 0);

  const addTagsPopoverVisible = bulkTagsPopover?.visible && bulkTagsPopover?.mode === 'add';
  const removeTagsPopoverVisible = bulkTagsPopover?.visible && bulkTagsPopover?.mode === 'remove';

  /**
   * Close bulk tags popover
   */
  const closeBulkTagsPopover = () => setBulkTagsPopover?.({ visible: false, mode: 'add' });

  /**
   * Add tags content
   * @returns
   */
  const addTagsContent = addTagsPopoverVisible && (
    <BulkTagsPopover
      mode="add"
      tags={tags}
      initialSelectedTagIds={bulkTagsPopover?.initialSelectedTagIds || []}
      onCancel={closeBulkTagsPopover}
      onConfirm={onBulkTagsConfirm}
      fetchTags={fetchTags}
    />
  );

  /**
   * Remove tags content
   * @returns
   */
  const removeTagsContent = removeTagsPopoverVisible && (
    <BulkTagsPopover
      mode="remove"
      tags={tags}
      initialSelectedTagIds={bulkTagsPopover?.initialSelectedTagIds || []}
      onCancel={closeBulkTagsPopover}
      onConfirm={onBulkTagsConfirm}
      fetchTags={fetchTags}
    />
  );

  /**
   * Render bulk actions bar
   * @returns
   */
  return (
    <div className="bulk-actions-bar">
      <div className="bulk-actions-bar__actions">
        {trash ? (
          <>
            <Button
              className="bulk-actions-bar__btn approve-btn"
              icon={<RestoreIcon />}
              onClick={onRestore}
              disabled={buttonsDisabled}>
              Restore
            </Button>
            <Button
              className="bulk-actions-bar__btn delete-btn"
              icon={<TrashIcon />}
              onClick={onDelete}
              disabled={buttonsDisabled}>
              Delete from trash
            </Button>
            {hasSelection && (
              <Tooltip title="Clear selection">
                <Button
                  className="bulk-actions-bar__btn clear-selection-btn"
                  icon={<CloseIcon />}
                  onClick={onClearSelection}>
                  Clear selection
                </Button>
              </Tooltip>
            )}
          </>
        ) : (
          <>
            <Button
              className="bulk-actions-bar__btn approve-btn"
              icon={<CircleTickIcon />}
              onClick={onApprove}
              disabled={buttonsDisabled}>
              Approve
            </Button>
            <Button
              className="bulk-actions-bar__btn decline-btn"
              icon={<FailedIcon />}
              onClick={onDecline}
              disabled={buttonsDisabled}>
              Decline
            </Button>
            <Divider type="vertical" />
            <Popover
              content={removeTagsContent}
              trigger="click"
              open={removeTagsPopoverVisible}
              onOpenChange={(open) => !open && closeBulkTagsPopover()}
              placement="bottomLeft"
              overlayClassName="bulk-tags-popover">
              <Button
                className="bulk-actions-bar__btn"
                onClick={() => onAddOrRemoveTags('remove')}
                disabled={buttonsDisabled}>
                Remove tags
              </Button>
            </Popover>
            <Popover
              content={addTagsContent}
              trigger="click"
              open={addTagsPopoverVisible}
              onOpenChange={(open) => !open && closeBulkTagsPopover()}
              placement="bottomLeft"
              overlayClassName="bulk-tags-popover">
              <Button
                className="bulk-actions-bar__btn"
                onClick={() => onAddOrRemoveTags('add')}
                disabled={buttonsDisabled}>
                Add tags
              </Button>
            </Popover>
            <Divider type="vertical" />
            <Button
              className="bulk-actions-bar__btn delete-btn"
              icon={<DeleteIcon />}
              onClick={onDelete}
              disabled={buttonsDisabled}>
              Delete
            </Button>
            <Divider type="vertical" />
            {hasSelection && (
              <Tooltip title="Clear selection">
                <Button
                  className="bulk-actions-bar__btn clear-selection-btn"
                  icon={<CloseIcon />}
                  onClick={onClearSelection}>
                  Clear selection
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Bulk actions bar prop types
 */
BulkActionsBar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  trash: PropTypes.bool.isRequired,
  onApprove: PropTypes.func,
  onDecline: PropTypes.func,
  onRestore: PropTypes.func,
  onAddOrRemoveTags: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired,
  isBulkActionLoading: PropTypes.bool,
  bulkTagsPopover: PropTypes.shape({
    visible: PropTypes.bool,
    mode: PropTypes.string,
    initialSelectedTagIds: PropTypes.array,
  }),
  setBulkTagsPopover: PropTypes.func,
  tagsFilter: PropTypes.array,
  onBulkTagsConfirm: PropTypes.func,
  fetchTags: PropTypes.func,
};

/**
 * Bulk actions bar default props
 */
BulkActionsBar.defaultProps = {
  onApprove: () => {},
  onDecline: () => {},
  onRestore: () => {},
  isBulkActionLoading: false,
  bulkTagsPopover: null,
  setBulkTagsPopover: () => {},
  tagsFilter: [],
  onBulkTagsConfirm: () => {},
  fetchTags: () => {},
};

export default BulkActionsBar;
